import { useEffect, useMemo, useState } from 'react'
import { AlertCircle, Check, Loader2 } from 'lucide-react'
import type { CreatedProduct } from '@/types/products'
import {
  useCapacitySummary,
  useFreeSlots,
  useRefreshStorageAvailability,
  useSetProductLocation,
  useStorageConditions,
} from '@/hooks/useStorage'
import {
  filterUnitsByCondition,
  freePositions,
  pickUnit,
} from '@/lib/productStorage'
import { profileLabel } from '@/lib/storageTempMapping'
import { formatLocation, pathSegments } from '@/lib/storageTree'
import { LocationTrail } from '@/components/inventory/storage/picker/LocationTrail'
import { StorageLocationPicker } from '@/components/inventory/storage/picker/StorageLocationPicker'
import {
  RequirementNotice,
  requiredConditionOf,
  unitConditionLabel,
  useStorageRequirement,
} from '@/components/procedure/StorageAssignmentDialog'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'

/** Nothing chosen yet for a row — distinct from "deliberately left unplaced". */
const LATER = ''

/** Slots a unit offers the rows at once — far more than any split has aliquots. */
const SLOT_PAGE = 200

/**
 * Place a whole batch of products — a split's aliquots — in one screen.
 *
 * The flow this replaces asked for one location, closed, then re-opened for
 * the next child with no indication that more were coming: an operator
 * splitting into four saw the same dialog four times and could not tell how
 * many remained, what they had already chosen, or whether two aliquots had
 * landed in the same slot. Doing them together is also how the work actually
 * happens — a split's aliquots go into one rack, in consecutive positions.
 *
 * So: pick the unit once, and every aliquot gets a consecutive free slot
 * pre-filled, each overridable per row. Slots already spoken for by another
 * row are removed from the other dropdowns, which is what makes double
 * booking within a batch impossible rather than merely unlikely.
 *
 * Assignment is per product against the same endpoint the single-product
 * dialog uses, so a partial failure leaves the successful rows placed and
 * says which ones did not — a batch that rolled itself back on one bad slot
 * would be worse, since the bags are already physically in the rack.
 *
 * Units and positions are narrowed to the batch's mapped storage condition, as
 * in the single-product dialog, read from the products' own catalog entries; an
 * unmapped temperature allows any unit.
 */
export function BatchStorageAssignmentDialog({
  products,
  onClose,
}: {
  /** The batch to place. Empty closes the dialog. */
  products: CreatedProduct[]
  onClose: () => void
}) {
  const open = products.length > 0

  const [browseUnitId, setBrowseUnitId] = useState<string | null>(null)
  const [choseUnit, setChoseUnit] = useState(false)
  /** productId → storage node id, or LATER. */
  const [slots, setSlots] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  /** productId → null on success, message on failure. Present once attempted. */
  const [results, setResults] = useState<Record<string, string | null>>({})

  const setProductLocation = useSetProductLocation()
  const refreshAvailability = useRefreshStorageAvailability()

  // Slot availability is volatile, so every open asks again.
  useEffect(() => {
    if (open) refreshAvailability()
  }, [open, refreshAvailability])

  const requirement = useStorageRequirement(products.map(p => p.catalogEntryId))
  const requires = requiredConditionOf(requirement)
  // Nothing is offered until the requirement is known.
  const blocked = requirement.status !== 'ready'
  const storageTemp = requirement.status === 'ready' ? requirement.storageTemp : undefined
  // Only while open: the dialog is mounted on every procedure page (z8nrz7e8kw).
  const summary = useCapacitySummary(open)
  const conditions = useStorageConditions().data?.conditions ?? []
  const byCondition = useMemo(
    () =>
      blocked
        ? { eligible: [], hidden: 0 }
        : filterUnitsByCondition(summary.data ?? [], requires),
    [summary.data, requires, blocked],
  )
  const picked = useMemo(
    () => pickUnit(byCondition.eligible, requires),
    [byCondition.eligible, requires],
  )

  const activeUnitId = choseUnit ? browseUnitId : (picked?.unit.nodeId ?? null)
  // One page of free slots, declared ones included (z8nrz7fqfy): a split's
  // aliquots pre-fill across a box's declared cells, not only its stored rows.
  const free = useFreeSlots(open ? activeUnitId : null, { condition: requires ?? undefined, limit: SLOT_PAGE })
  const available = useMemo(
    () => (free.data ? freePositions(free.data.nodes, SLOT_PAGE, requires) : []),
    [free.data, requires],
  )

  // Pre-fill consecutive free slots, one per product, whenever the unit
  // changes. This is the whole convenience: aliquots from one split go into
  // one rack in order, so the default should already be right.
  useEffect(() => {
    if (!open) return
    const next: Record<string, string> = {}
    products.forEach((p, i) => {
      next[p.id] = available[i]?.id ?? LATER
    })
    setSlots(next)
    setResults({})
  }, [activeUnitId, available, open, products])

  const unitsWithRoom = useMemo(() => byCondition.eligible.filter(u => u.remaining > 0), [byCondition.eligible])
  const activeUnit = unitsWithRoom.find(u => u.nodeId === activeUnitId) ?? null
  const [choosing, setChoosing] = useState(false)
  /** A slot as the row's dropdown names it: the unit is said once above, so only what is inside it. */
  const slotLabel = (path: string) => formatLocation(pathSegments(path).slice(1).join('/'))

  const takenElsewhere = (productId: string) =>
    new Set(
      Object.entries(slots)
        .filter(([id, nodeId]) => id !== productId && nodeId !== LATER)
        .map(([, nodeId]) => nodeId),
    )

  const placedCount = Object.values(slots).filter(v => v !== LATER).length
  const shortfall = products.length - available.length
  // A cached "no units" answer must never decide where a batch goes: with no
  // room on record, wait for the fresh answer before saying so.
  const loading =
    summary.isLoading ||
    (summary.isFetching && unitsWithRoom.length === 0) ||
    (activeUnitId != null && free.isLoading)
  const attempted = Object.keys(results).length > 0
  const failures = Object.entries(results).filter(([, err]) => err != null)

  function handleClose() {
    setBrowseUnitId(null)
    setChoseUnit(false)
    setChoosing(false)
    setSlots({})
    setResults({})
    setProductLocation.reset()
    onClose()
  }

  async function assignAll() {
    setSaving(true)
    const outcome: Record<string, string | null> = {}
    for (const p of products) {
      const nodeId = slots[p.id]
      if (!nodeId) continue
      try {
        await setProductLocation.mutateAsync({ productId: p.id, storageNodeId: nodeId })
        outcome[p.id] = null
      } catch (e) {
        outcome[p.id] = e instanceof Error ? e.message : 'Could not place this product.'
      }
    }
    setResults(outcome)
    setSaving(false)
    // Everything asked for landed — nothing left to look at.
    if (!Object.values(outcome).some(v => v != null)) handleClose()
  }

  return (
    <Dialog open={open} onOpenChange={o => !o && handleClose()}>
      <DialogContent className='sm:max-w-3xl'>
        <DialogHeader>
          <DialogTitle>
            Storage locations — {products.length} product{products.length === 1 ? '' : 's'}
          </DialogTitle>
          <DialogDescription>
            Confirm where each one goes. Locations are suggested from the storage profile
            {storageTemp ? ` (${storageTemp})` : ''}; change any of them before assigning.
          </DialogDescription>
        </DialogHeader>

        {blocked ? (
          <RequirementNotice requirement={requirement} hidden={0} conditions={conditions} />
        ) : loading ? (
          <div className='flex items-center gap-2 p-6 text-sm text-muted-foreground'>
            <Loader2 className='h-4 w-4 animate-spin' />
            Finding available locations…
          </div>
        ) : (
          <div className='space-y-3'>
            <RequirementNotice requirement={requirement} hidden={byCondition.hidden} conditions={conditions} />
            <div className='space-y-3 rounded-xl border bg-muted/20 p-3'>
              <div className='flex flex-wrap items-start justify-between gap-2'>
                <div className='min-w-0 flex-1'>
                  <p className='text-[11px] font-medium uppercase tracking-wider text-muted-foreground'>Storage unit</p>
                  {activeUnit ? (
                    <>
                      <LocationTrail path={activeUnit.code} className='mt-1' />
                      <p className='mt-0.5 text-xs text-muted-foreground'>
                        {activeUnit.name ? `${activeUnit.name} · ` : ''}
                        {unitConditionLabel(activeUnit.condition, conditions)} ·{' '}
                        {(free.data?.freeTotal ?? 0).toLocaleString()} free slot
                        {free.data?.freeTotal === 1 ? '' : 's'}
                      </p>
                    </>
                  ) : (
                    <p className='mt-1 text-sm font-medium'>
                      {requires ? `No unit at ${profileLabel(requires, conditions)} has room` : 'None available'}
                    </p>
                  )}
                </div>
                {unitsWithRoom.length > 0 && (
                  <Button variant='outline' size='sm' onClick={() => setChoosing(c => !c)}>
                    {choosing ? 'Done' : 'Choose a different unit'}
                  </Button>
                )}
              </div>
              {choosing && (
                <StorageLocationPicker
                  mode='unit'
                  units={unitsWithRoom}
                  requires={requires}
                  conditions={conditions}
                  initialUnitId={activeUnitId}
                  selectedUnitId={activeUnitId}
                  onSelectUnit={unit => {
                    setBrowseUnitId(unit.nodeId)
                    setChoseUnit(true)
                    setChoosing(false)
                  }}
                />
              )}
              {free.isError && (
                <p className='flex items-start gap-2 text-sm text-destructive'>
                  <AlertCircle className='mt-0.5 h-4 w-4 shrink-0' />
                  Could not load this unit's slots: {(free.error as Error)?.message ?? 'unknown error'}
                </p>
              )}
            </div>

            {shortfall > 0 && (
              <p className='flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900'>
                <AlertCircle className='mt-0.5 h-4 w-4 shrink-0' />
                This unit has {available.length} free slot{available.length === 1 ? '' : 's'} for{' '}
                {products.length} products. {shortfall} will stay unplaced unless you pick another
                unit — you can assign the rest later from Manage Products.
              </p>
            )}

            <div className='max-h-[22rem] overflow-y-auto rounded-lg border'>
              <table className='w-full text-sm'>
                <thead className='sticky top-0 bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground'>
                  <tr>
                    <th className='px-3 py-2 text-left font-medium'>Product</th>
                    <th className='px-3 py-2 text-left font-medium'>Volume</th>
                    <th className='px-3 py-2 text-left font-medium'>Location</th>
                    <th className='w-8' />
                  </tr>
                </thead>
                <tbody>
                  {products.map(p => {
                    const taken = takenElsewhere(p.id)
                    const chosen = slots[p.id] ?? LATER
                    const err = results[p.id]
                    return (
                      <tr key={p.id} className='border-t'>
                        <td className='px-3 py-2'>
                          <span className='block font-mono text-xs'>{p.din}</span>
                          <span className='block text-xs text-muted-foreground'>
                            {p.productDescriptionCode || '—'}
                          </span>
                        </td>
                        <td className='px-3 py-2 text-muted-foreground'>
                          {p.volumeMl != null ? `${p.volumeMl} mL` : '—'}
                        </td>
                        <td className='px-3 py-2'>
                          <select
                            aria-label={`Location for ${p.din} ${p.productDescriptionCode}`}
                            value={chosen}
                            disabled={saving}
                            onChange={e =>
                              setSlots(prev => ({ ...prev, [p.id]: e.target.value }))
                            }
                            className='w-full rounded-md border bg-background px-2 py-1.5 text-sm'
                          >
                            <option value={LATER}>Assign later</option>
                            {available
                              .filter(n => n.id === chosen || !taken.has(n.id))
                              .map(n => (
                                <option key={n.id} value={n.id}>
                                  {slotLabel(n.path)}
                                </option>
                              ))}
                          </select>
                          {err && <p className='mt-1 text-xs text-destructive'>{err}</p>}
                        </td>
                        <td className='px-2'>
                          {attempted && err === null && (
                            <Check className='h-4 w-4 text-green-600' aria-label='Placed' />
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {failures.length > 0 && (
              <p className='text-sm text-destructive'>
                {failures.length} of {products.length} could not be placed. The others were placed
                — fix these and assign again, or place them later from Manage Products.
              </p>
            )}
          </div>
        )}

        <DialogFooter className='sm:justify-between'>
          <span className='text-sm text-muted-foreground'>
            {placedCount} of {products.length} will be placed
          </span>
          <div className='flex gap-2'>
            <Button variant='outline' onClick={handleClose} disabled={saving}>
              {placedCount === 0 ? 'Close' : 'Cancel'}
            </Button>
            <Button onClick={assignAll} disabled={saving || placedCount === 0}>
              {saving ? 'Assigning…' : `Assign ${placedCount}`}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
